import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActions,
  AppBar,
  Toolbar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useSections } from "./SectionContext";

const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const Volunteering = () => {
  const [content, setContent] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [records, setRecords] = useState([]);
  const [editIndex, setEditIndex] = useState(null);

  const { sections } = useSections();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  // Load from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("volunteering")) || [];
    setRecords(saved);
  }, []);

  // Save to localStorage
  const saveToLocalStorage = (data) => {
    localStorage.setItem("volunteering", JSON.stringify(data));
  };

  const handleEdit = (index) => {
    const rec = records[index];
    setSubtopic(rec.subtopic);
    setFromDate(rec.fromDate);
    setToDate(rec.toDate);
    setContent(rec.content);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    const updated = records.filter((_, i) => i !== index);
    setRecords(updated);
    saveToLocalStorage(updated);
  };

  const handleSaveAndNext = () => {
    // Only save a new/edited record if subtopic AND content are filled
    if (subtopic.trim() && content.trim()) {
      let updatedRecords;
      if (editIndex !== null) {
        updatedRecords = records.map((rec, idx) =>
          idx === editIndex ? { subtopic, fromDate, toDate, content } : rec
        );
        setEditIndex(null);
      } else {
        updatedRecords = [...records, { subtopic, fromDate, toDate, content }];
      }

      setRecords(updatedRecords);
      saveToLocalStorage(updatedRecords);

      setSubtopic("");
      setFromDate("");
      setToDate("");
      setContent("");
    }

    // Always navigate to next section regardless of form state
    const currentSection = "Volunteering";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex !== -1 && currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", { state: { selectedTemplate, selectedTheme } });
    }
  };

  // Back: just go back, no data changes
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: "#002B5B" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBack} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: "bold", letterSpacing: 0.5 }}>
            Volunteering
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          maxWidth: "900px",
          mx: "auto",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#002B5B",
            fontWeight: "bold",
            mb: 2,
            borderBottom: "2px solid #0288D1",
            pb: 1,
          }}
        >
          Tell us about your volunteer work.
        </Typography>

        <TextField
          fullWidth
          label="Subtopic"
          value={subtopic}
          onChange={(e) => setSubtopic(e.target.value)}
          sx={{
            mb: 2,
            backgroundColor: "#ffffff",
            borderRadius: 1,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#B3D9F7" },
              "&:hover fieldset": { borderColor: "#0288D1" },
              "&.Mui-focused fieldset": { borderColor: "#002B5B" },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
          }}
        />

        <Box
          display="flex"
          gap={2}
          sx={{
            mb: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#B3D9F7" },
                "&:hover fieldset": { borderColor: "#0288D1" },
                "&.Mui-focused fieldset": { borderColor: "#002B5B" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
            }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#B3D9F7" },
                "&:hover fieldset": { borderColor: "#0288D1" },
                "&.Mui-focused fieldset": { borderColor: "#002B5B" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
            }}
          />
        </Box>

        <TextField
          id="volunteering-box"
          fullWidth
          multiline
          rows={6}
          placeholder="Tell us about your volunteer work"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: 2,
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#B3D9F7" },
              "&:hover fieldset": { borderColor: "#0288D1" },
              "&.Mui-focused fieldset": { borderColor: "#002B5B" },
            },
          }}
        />

        {/* Save & Next Button — always enabled */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAndNext}
            sx={{
              px: 4,
              py: 1,
              fontWeight: "bold",
              backgroundColor: "#002B5B",
              "&:hover": { backgroundColor: "#0288D1" },
              borderRadius: 2,
              boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
            }}
          >
            Save & Next
          </Button>
        </Box>

        {/* Display saved records */}
        <Box mt={4}>
          {records.map((rec, idx) => (
            <Card
              key={idx}
              sx={{
                mb: 2,
                boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                border: "1px solid #B3D9F7",
                borderLeft: "4px solid #002B5B",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ color: "#002B5B", fontWeight: "bold" }}>
                  {rec.subtopic}
                </Typography>
                <Typography variant="body2" sx={{ color: "#0288D1", mt: 0.5 }}>
                  {rec.fromDate} - {rec.toDate}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: "#0D4C8B" }}>
                  {rec.content}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton
                  onClick={() => handleEdit(idx)}
                  sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => handleDelete(idx)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Volunteering;